import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import type { LinkProps } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import LinkIcon from '@mui/icons-material/Link';
import LockIcon from '@mui/icons-material/Lock';
import VpnLockIcon from '@mui/icons-material/VpnLock';
import {
  VillaOutlined,
  VolunteerActivismOutlined,
  PersonOutlineOutlined,
} from '@mui/icons-material';
import { capitalize } from 'lodash';
import { Chip } from '@mui/material';

import { DeedListDateInfoWithStatus } from 'components/DeedListDateInfoWithStatus';
import Img from 'retired/Img';
import ProfileIcon from 'assets/icons/profile.svg';
import ExternalLinkBlackIcon from 'assets/icons/external-link-black.svg';
import { teal, blue, primaryBlue, yellow, orange, black, gray3 } from 'utils/color-styles';
import { currenciesFormat } from 'utils/currencyFormat';
import sumObjects from 'utils/sumObjects';
import { Features } from 'utils/features';
import OrganizationChip from 'components/OrganizationChip';
import { DateRangeWithTimeZone } from 'components/DeedListDateInfoWithStatus/DateRangeWithTimeZone';
import DeedModel from 'entities/deed/model';
import Cause from 'entities/cause/model';
import User from 'entities/user/model';
import Location from 'entities/location/model';

const Link = RouterLink as unknown as React.ComponentClass<LinkProps>;

const deedStyles = {
  backgroundColor: '#fff',
  boxShadow: '0 1px 6px rgba(199, 205, 235, 0.2)',
  marginBottom: 10,
  cursor: 'pointer',
  position: 'relative',
  borderRightStyle: 'solid',
  borderRightWidth: 5,
  listStyleType: 'none',
};

interface Props {
  deed: DeedModel;
  causes: Cause[];
  isCompany: boolean;
  user: User;
  locations: Location[];
}

export const Deed = ({ deed, causes, isCompany, locations, user }: Props) => {
  const attendees = deed.attendees.length;
  const pendingApplicants = deed.getNumberOfUsersByRoleAndStatus();
  const interested = deed.externalLinkFollowsCount;

  const totalSpots =
    deed.roles?.reduce(
      (previousValue, currentRole) => previousValue + currentRole.get('requiredAmount'),
      0
    ) || 0;

  const goalAmountUserCurrency = deed.goalAmountCurrencies[user.displayCurrency] || 0;
  const donatedAmountUserCurrency = deed.donatedAmountCurrencies[user.displayCurrency] || 0;
  const matchedAmountUserCurrency = deed.matchedAmountCurrencies[user.displayCurrency] || 0;

  const useNewDeedsPage = user?.hasFeature(Features.PAGINATED_DEEDS_SEARCH);

  const submitterLabel =
    {
      Employee: (
        <>
          <PersonOutlineOutlined color={gray3} /> Created by Employee
        </>
      ),
      Nonprofit: (
        <>
          <VolunteerActivismOutlined color={gray3} /> Created by Nonprofit
        </>
      ),
      Company: (
        <>
          <VillaOutlined color={gray3} /> Created by Company
        </>
      ),
    }[deed.submitter?.type === 'User' ? 'Employee' : deed.submitter?.organization?.type] || null;

  const accentColor =
    deed.ended && deed.status === 'published'
      ? primaryBlue
      : deed.status === 'draft'
      ? orange
      : deed.status === 'pending'
      ? '#FFEC4C'
      : '#4DAD80';

  const chipColor =
    deed.ended && deed.status === 'published'
      ? 'info'
      : deed.status === 'draft'
      ? 'warning'
      : deed.status === 'pending'
      ? 'secondary'
      : 'success';

  return (
    /* eslint-disable jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions,jsx-a11y/click-events-have-key-events */
    <li
      style={{
        ...deedStyles,
        borderRightColor: accentColor,
      }}
      title={deed.status}
    >
      <Link
        to={`/deeds/${deed.pathSlug}/${deed.id}`}
        key={deed.id}
        style={{ display: 'flex', color: black }}
      >
        {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions,jsx-a11y/click-events-have-key-events */}
        {deed.type !== 'Project' && (
          <>
            <DeedListDateInfoWithStatus
              ended={deed.ended}
              status={deed.status}
              date={deed.startingAt}
            ></DeedListDateInfoWithStatus>
          </>
        )}
        <Grid
          style={{
            flexGrow: 1,
            marginLeft: 28,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Grid container alignItems="center">
            <Grid item xs style={{ height: '100%', margin: '0 !important' }}>
              <h3
                style={{
                  fontSize: 16,
                  fontWeight: 500,
                  margin: '0px 0px 7px',
                }}
              >
                {deed.name}
                {deed.linkOnly && (
                  <span title="Link only" style={{ marginLeft: 7 }}>
                    <LinkIcon style={{ fontSize: 20, verticalAlign: 'top' }} />
                  </span>
                )}
                {deed.requireAuthentication && (
                  <span title="Authentication required" style={{ marginLeft: 7 }}>
                    <LockIcon style={{ fontSize: 20, verticalAlign: 'top' }} />
                  </span>
                )}
                {deed.allowedLocations?.size > 0 && (
                  <span
                    title={`Allowed locations: ${deed.allowedLocations
                      .slice(0, 10)
                      .map((locationId) => locations?.get(locationId)?.name)
                      .join(', ')}${deed.allowedLocations?.size > 10 ? '...' : ''}`}
                    style={{ marginLeft: 7 }}
                  >
                    <VpnLockIcon style={{ fontSize: 20, verticalAlign: 'top' }} />
                  </span>
                )}
                {deed.externalLink && (
                  <span title="External" style={{ marginLeft: 7 }}>
                    <Img
                      alt="External Link Icon"
                      src={ExternalLinkBlackIcon}
                      style={{
                        width: 20,
                        height: 20,
                        verticalAlign: 'text-bottom',
                      }}
                    />
                  </span>
                )}
              </h3>
              <div style={{ fontSize: 12 }}>
                {deed.virtual
                  ? 'Virtual'
                  : [deed.locationObject.area, deed.locationObject.city, deed.locationObject.state]
                      .filter(Boolean)
                      .join(', ')}
              </div>
              {deed.startingAt && (
                <DateRangeWithTimeZone
                  startingAt={deed.startingAt}
                  timeZone={deed.timeZone}
                  endingAt={deed.endingAt}
                  withTime={['Event', 'BaseEvent'].includes(deed.type)}
                />
              )}
            </Grid>
            <Grid item xs>
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <h4
                    style={{
                      fontSize: 15,
                      fontWeight: 300,
                      marginBottom: 10,
                    }}
                  >
                    {isCompany
                      ? (deed.organization?.id !== user.organization.id &&
                          deed.organization?.name) ||
                        deed.nonprofits
                          ?.toArray()
                          ?.map((nonprofit) => nonprofit?.get('name'))
                          ?.join(', ')
                      : deed.partner?.name}
                  </h4>

                  {causes?.size > 0 &&
                    deed.categories.slice(0, 3).map((categoryId) => {
                      const category = causes.get(categoryId);
                      return (
                        category && (
                          <div
                            style={{ height: 24, marginLeft: -5 }}
                            key={`${deed.id}-category-${categoryId}`}
                          >
                            <span
                              style={{
                                marginLeft: 5,
                                marginRight: 5,
                                marginBottom: 5,
                                height: 26,
                                verticalAlign: 'top',
                                backgroundColor: '#ed3c64',
                                padding: 5,
                                color: 'white',
                                fontSize: 11,
                                borderRadius: 5,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {category.name}
                            </span>
                          </div>
                        )
                      );
                    })}

                  <div
                    style={{
                      fontSize: 12,
                      color: '#ADADC3',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                    }}
                  >
                    {['Event', 'BaseEvent'].includes(deed.type) && (
                      <>
                        <div
                          style={{
                            display: 'flex',
                            width: 100,
                            height: 12,
                            backgroundColor: '#EBEEF0',
                            borderRadius: 12,
                            flexDirection: 'row',
                            marginRight: 10,
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              width: `${(deed.attendees.length / deed.attendeeLimit) * 100}%`,
                              height: 12,
                              backgroundColor: blue,
                              borderTopRightRadius: matchedAmountUserCurrency ? 0 : 12,
                              borderBottomRightRadius: matchedAmountUserCurrency ? 0 : 12,
                              borderTopLeftRadius: 12,
                              borderBottomLeftRadius: 12,
                            }}
                          />
                        </div>
                        <Img
                          src={ProfileIcon}
                          alt=""
                          style={{
                            width: 12,
                            height: 12,
                            verticalAlign: 'baseline',
                          }}
                        />
                        <span
                          style={{
                            color: '#ADADC3',
                            marginLeft: 12,
                            marginRight: 12,
                            verticalAlign: 'top',
                          }}
                        >
                          {deed.attendees.length}
                          {deed.attendeeLimit > 0 &&
                            deed.attendees.length <= deed.attendeeLimit &&
                            `/${deed.attendeeLimit}`}{' '}
                          Attendees
                        </span>
                        {deed.waitlist.length > 0 && (
                          <span
                            style={{
                              color: '#ADADC3',
                              marginRight: 12,
                              verticalAlign: 'top',
                            }}
                          >
                            Waitlist: {deed.waitlist.length}
                          </span>
                        )}
                      </>
                    )}
                    {deed.type === 'Campaign' && !useNewDeedsPage && (
                      <>
                        <div
                          style={{
                            display: 'flex',
                            width: 100,
                            height: 12,
                            backgroundColor: '#EBEEF0',
                            borderRadius: 12,
                            flexDirection: 'row',
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              width: `${
                                (!goalAmountUserCurrency ||
                                donatedAmountUserCurrency + matchedAmountUserCurrency >
                                  goalAmountUserCurrency
                                  ? donatedAmountUserCurrency /
                                    (donatedAmountUserCurrency + matchedAmountUserCurrency)
                                  : donatedAmountUserCurrency / goalAmountUserCurrency) * 100
                              }%`,
                              height: 12,
                              backgroundColor: teal,
                              borderTopRightRadius: matchedAmountUserCurrency ? 0 : 12,
                              borderBottomRightRadius: matchedAmountUserCurrency ? 0 : 12,
                              borderTopLeftRadius: 12,
                              borderBottomLeftRadius: 12,
                            }}
                          />
                          {matchedAmountUserCurrency ? (
                            <div
                              style={{
                                width: `${
                                  (!goalAmountUserCurrency ||
                                  donatedAmountUserCurrency + matchedAmountUserCurrency >
                                    goalAmountUserCurrency
                                    ? matchedAmountUserCurrency /
                                      (donatedAmountUserCurrency + matchedAmountUserCurrency)
                                    : matchedAmountUserCurrency / goalAmountUserCurrency) * 100
                                }%`,
                                height: 12,
                                backgroundColor: yellow,
                                borderTopLeftRadius: donatedAmountUserCurrency ? 0 : 12,
                                borderBottomLeftRadius: donatedAmountUserCurrency ? 0 : 12,
                                borderTopRightRadius: 12,
                                borderBottomRightRadius: 12,
                              }}
                            />
                          ) : null}
                        </div>
                        <div
                          style={{
                            color: '#ADADC3',
                            marginLeft: 10,
                            verticalAlign: 'text-bottom',
                          }}
                        >
                          {currenciesFormat(deed.donatedAmountCurrencies, user.displayCurrency)}
                          {user.displayCurrency !== deed.currencyCode ? '*' : ''}{' '}
                          {matchedAmountUserCurrency ? (
                            <>
                              donated +{' '}
                              {currenciesFormat(deed.matchedAmountCurrencies, user.displayCurrency)}
                              {user.displayCurrency !== deed.currencyCode ? '*' : ''} matched ={' '}
                              {currenciesFormat(
                                sumObjects(
                                  deed.donatedAmountCurrencies,
                                  deed.matchedAmountCurrencies
                                ),
                                user.displayCurrency
                              )}
                              {user.displayCurrency !== deed.currencyCode ? '*' : ''}
                            </>
                          ) : null}{' '}
                          raised of{' '}
                          {currenciesFormat(deed.goalAmountCurrencies, user.displayCurrency)}
                          {user.displayCurrency !== deed.currencyCode ? '*' : ''}
                        </div>
                      </>
                    )}
                    {deed.type === 'Project' && (
                      <>
                        <div
                          style={{
                            display: 'flex',
                            width: 100,
                            height: 12,
                            backgroundColor: '#EBEEF0',
                            borderRadius: 12,
                            flexDirection: 'row',
                            marginRight: 10,
                            position: 'relative',
                            overflow: 'hidden',
                          }}
                        >
                          {(pendingApplicants > 0 || interested > 0) && (
                            <div
                              style={{
                                width: `${
                                  (totalSpots > 0
                                    ? (attendees + pendingApplicants + interested) / totalSpots
                                    : 0) * 100
                                }%`,
                                height: 12,
                                backgroundColor: orange,
                                borderRadius: 12,
                                opacity: 0.3,
                                position: 'absolute',
                              }}
                            />
                          )}
                          <div
                            style={{
                              width: `${(totalSpots > 0 ? attendees / totalSpots : 0) * 100}%`,
                              height: 12,
                              backgroundColor: orange,
                              borderRadius: 12,
                            }}
                          />
                        </div>

                        <Img
                          src={ProfileIcon}
                          alt=""
                          style={{
                            width: 12,
                            height: 12,
                            verticalAlign: 'baseline',
                          }}
                        />
                        <span
                          style={{
                            color: '#ADADC3',
                            marginLeft: 12,
                            marginRight: 12,
                            verticalAlign: 'top',
                          }}
                        >
                          {attendees}
                          {totalSpots > 0 && attendees <= totalSpots && `/${totalSpots}`} Approved
                          {pendingApplicants > 0 && `, ${pendingApplicants} Pending`}
                          {interested > 0 && `, ${interested} Interested`}
                        </span>
                      </>
                    )}
                    {deed.roles && deed.roles.size > 0 && (
                      <span style={{ color: '#ADADC3' }}>
                        {deed.type === 'Project' ? 'Roles needed' : 'Shifts needed'}:{' '}
                        {deed.roles.map((role) => role.get('name')).join(', ')}
                      </span>
                    )}
                  </div>
                </Box>
                {deed.isSharedWithSubsidiary && <OrganizationChip organization={deed.partner} />}
              </Box>
            </Grid>
          </Grid>
        </Grid>

        <Box
          p={1}
          display="flex"
          flexDirection="column"
          alignItems="flex-end"
          justifyContent="center"
        >
          <Chip
            size="small"
            label={
              deed.ended && deed.status === 'published' ? 'Completed' : capitalize(deed.status)
            }
            color={chipColor}
          />
          {submitterLabel && <Chip style={{ marginTop: 8 }} size="small" label={submitterLabel} />}
        </Box>
      </Link>
    </li>
  );
};
